/* global axios */
import ApiClient from '../ApiClient';

// M29.2 Voice Experience — thin REST client over the M29.1 Voice Foundation's
// minimal HTTP surface (app/controllers/api/v1/accounts/nexceo/voice/).
// Deliberately separate from api/channel/voice/voiceAPIClient.js (the
// Twilio-only client backing the legacy stores/calls.js) — this client
// speaks the new, provider-agnostic Nexceo::Voice::Session contract.
class NexceoVoiceAPI extends ApiClient {
  constructor() {
    super('nexceo/voice', { accountScoped: true });
  }

  // M29.2.1 Voice Reliability — the rehydration endpoint. Returns every
  // non-terminal call the current user can access, each with its precise
  // (meta-derived) state. Called on mount and on every ActionCable
  // reconnect — see NexceoVoiceListener.vue.
  listActiveSessions() {
    return axios.get(`${this.url}/sessions`).then(response => response.data);
  }

  getCapabilities(conversationId) {
    return axios
      .get(`${this.url}/capabilities`, {
        params: { conversation_id: conversationId },
      })
      .then(response => response.data);
  }

  startCall({ conversationId, provider, to, sdpOffer } = {}) {
    return axios
      .post(`${this.url}/sessions`, {
        conversation_id: conversationId,
        provider,
        to,
        sdp_offer: sdpOffer,
      })
      .then(response => response.data);
  }

  getSession(callId) {
    return axios.get(`${this.url}/sessions/${callId}`).then(r => r.data);
  }

  acceptCall(callId, { sdpAnswer } = {}) {
    return axios
      .post(`${this.url}/sessions/${callId}/accept`, { sdp_answer: sdpAnswer })
      .then(response => response.data);
  }

  rejectCall(callId) {
    return axios
      .post(`${this.url}/sessions/${callId}/reject`)
      .then(response => response.data);
  }

  endCall(callId) {
    return axios
      .post(`${this.url}/sessions/${callId}/terminate`)
      .then(response => response.data);
  }

  markMissed(callId) {
    return axios
      .post(`${this.url}/sessions/${callId}/miss`)
      .then(response => response.data);
  }

  // M29.3 Voice CRM Integration — pre-call context (lead/project/documents/
  // site visits/brief) and post-call wrap-up, backed by
  // Nexceo::Voice::CallsController. Kept on this same client rather than a
  // new one — same `nexceo/voice` resource, just a `calls/` sub-path
  // alongside the existing `sessions/` one.
  getCallContext(callId) {
    return axios.get(`${this.url}/calls/${callId}/context`).then(r => r.data);
  }

  getSummaryDraft(callId, { notes } = {}) {
    return axios
      .get(`${this.url}/calls/${callId}/summary_draft`, { params: { notes } })
      .then(r => r.data);
  }

  wrapUpCall(callId, { summary, notes, outcome } = {}) {
    return axios
      .post(`${this.url}/calls/${callId}/wrap_up`, { summary, notes, outcome })
      .then(r => r.data);
  }
}

export default new NexceoVoiceAPI();
